/**
 * analytics-overview-api.ts
 *
 * Central API service for the Analytics Overview page.
 * Exports used by chart-specific API files (best-in-category-api, etc.)
 * and by the AnalyticsSchedulePanel.
 */
import type { RunCompleteness } from "@/api/run-completeness";
import { apiFetch } from "../../../api/api-fetch";
import { triggerAIBehaviorRefresh } from "./ai-behavior-api";
import { normalizeProviderKey } from "@/app/constants/models";
import { fetchDeepSignalSeries, SEGMENT_KEY, type DSSeriesPoint } from "./deep-signal-series-api";
import type { SeriesRebaseline } from "@/api/series-rebaseline";

export function getActiveBrand(): string {
  return (
    localStorage.getItem("bm_brand") ||
    localStorage.getItem("brand_name") ||
    ""
  );
}

// Parse a backend series date into a real epoch (ms).
//
// The backend serialises snapshot dates as "%b %d" (e.g. "Jun 23"), with a
// "#2" suffix when two runs share a calendar date. That string has NO year, so
// Date.parse() interprets it inconsistently (often year 2001) which scatters
// points across decades once a real-dated projection is on the same axis. We
// strip the "#n" dedup suffix and pin the current year so every point sits on
// one coherent timeline. Returns NaN if still unparseable (caller falls back to
// the sequential index).
export function parseSeriesDate(raw: string | undefined | null): number {
  if (!raw) return NaN;
  const clean = String(raw).replace(/\s*#\d+\s*$/, "").trim();
  const t = Date.parse(`${clean} ${new Date().getFullYear()}`);
  return Number.isNaN(t) ? Date.parse(clean) : t;
}

// Scope the measured HISTORY shown on the chart. The Performance chart blends
// backward context with the forward projection, so this only controls how far
// back the real points go; the projection is always drawn forward on top.
//   "baseline": anchor on the most recent measurement (the Deep Signal run).
//                The chart starts there and builds the proposed roadmap forward.
//   "full": all measured history, for looking further back for context.
// Calendar-day windows (7D/30D/90D) were dropped: measurements arrive on a
// cadence (roughly monthly), so "last N days" was ambiguous and confusing.
export type SeriesRange = "baseline" | "full";
export function windowSeriesByRange<T>(series: T[], range: SeriesRange): T[] {
  if (!series.length) return series;
  return range === "full" ? series : series.slice(-1);
}

// ── Core types ────────────────────────────────────────────────────────────────

export interface TimeSeriesPoint {
  date: string;
  overall: number;
  [modelKey: string]: number | string;
}

/** Single data point synthesised from antennaData when no DB snapshots exist. */
export interface SyntheticSeriesPoint {
  date: string;
  timestamp: number;
  overall: number;
  [modelKey: string]: number | string;
}

export interface AnalyticsTimeSeriesData {
  brand_name: string;
  days: number;
  selected_models: string[];
  snapshot_count: number;
  summary: {
    perception_score: number;
    referral_rate: number;
    best_in_category_pct: number;
    number_one_recommendation_pct: number;
  };
  perception_series: TimeSeriesPoint[];
  referral_series: TimeSeriesPoint[];
  best_in_category_series: TimeSeriesPoint[];
  number_one_series: TimeSeriesPoint[];
}

export interface ScheduleConfig {
  id: number | null;
  is_enabled: boolean;
  interval_days: number;
  brands: string[];
  last_run_at: string | null;
  next_run_at: string | null;
  status: string;
  last_error: string | null;
  updated_by: string | null;
  scheduler_running: boolean;
}

export interface PromptTrackingData {
  totalPrompts: number;
  promptLimit: number;
  avgRecommendationRate: number;
  trendDirection: "up" | "down" | "stable";
  // Trend strings are null when only one measurement exists (no prior run to
  // compare against); the UI shows a neutral first-measurement state instead.
  trendValue: string | null;
  shareOfVoice: number;
  shareOfVoiceTrend: string | null;
  shareOfVoiceRank: number;
  totalCompetitors: number;
  sentimentScore: number;
  sentimentTrend: string | null;
  // Top-5 rate (percent). Sourced from Deep Signal when runs exist; the chart's
  // funnel previously approximated this as shareOfVoice * 0.65.
  top5Rate: number;
  top5Trend: string | null;
  primaryRecTrend: string | null;
  topPerformers: string[];
  needsOptimization: string[];
  // The latest Deep Signal run's incomplete-run verdict, when the funnel KPIs
  // come from Deep Signal. Shown beside the KPI tiles only when incomplete.
  latestCompleteness?: RunCompleteness | null;
  // The latest Deep Signal read is a series rebaseline's NEW BASELINE: its
  // funnel trends are not computed against the read before it (measured on a
  // different battery), and the tiles say "New baseline" instead.
  latestIsNewBaseline?: boolean;
  latestRebaseline?: SeriesRebaseline | null;
}

export interface AIInsight {
  id: string;
  type: "success" | "warning" | "achievement" | "opportunity";
  iconBg: string;
  iconColor: string;
  metric: string;
  title: string;
  subtitle: string;
  details: string;
  confidence: number;
  source: string;
  timestamp: string;
  aiReasoning: string;
}

export type MetricKey = "perception" | "referral" | "bestInCategory" | "numberOne";

// ── Model key normalisation ───────────────────────────────────────────────────
// Backwards-compatible alias for the central helper. New code should import
// `normalizeProviderKey` directly from `@/app/constants/models`.

export function normaliseModelKey(raw: string): string {
  return normalizeProviderKey(raw);
}

export function extractSelectedModels(antennaData: any, fallbackModels?: string[]): string[] {
  if (!antennaData || typeof antennaData !== "object") return fallbackModels ?? [];
  const modelComparison: any[] = antennaData.modelComparison ?? [];
  const keys = [...new Set(
    modelComparison
      .map(item => normalizeProviderKey(String(item?.model ?? "")))
      .filter(k => Boolean(k)),
  )];
  return keys.length > 0 ? keys : (fallbackModels ?? []);
}

// ── Synthetic series builder ──────────────────────────────────────────────────
// Used by chart-specific API files when no DB snapshots exist yet.

export function extractMetricFromAntennaData(
  antennaData: any,
  metric: MetricKey,
): { overall: number; perModel: Record<string, number> } | null {
  if (!antennaData || typeof antennaData !== "object") return null;

  const hero       = antennaData.heroMetrics    ?? {};
  const perception = antennaData.perceptionData ?? {};
  const modelComp: any[]         = antennaData.modelComparison     ?? [];
  const exactPerModel: Record<string, any> = antennaData.perModelBusinessMetrics ?? {};

  let overall = 0;
  switch (metric) {
    case "perception":     overall = parseFloat(perception.overallScore                  ?? 0); break;
    case "referral":       overall = parseFloat(hero.referralConversations?.percentage   ?? 0); break;
    case "bestInCategory": overall = parseFloat(hero.bestInCategory?.percentage          ?? 0); break;
    case "numberOne":      overall = parseFloat(hero.numberOneRecommendation?.percentage ?? 0); break;
  }

  const overallPerception = parseFloat(perception.overallScore ?? 0) || 1;
  const perceptionByModel: Record<string, number> = {};
  for (const item of modelComp) {
    if (!item || typeof item !== "object") continue;
    const key = normalizeProviderKey(String(item.model ?? ""));
    if (!key) continue;
    perceptionByModel[key] = parseFloat(item.score ?? overallPerception);
  }

  const perModel: Record<string, number> = {};
  for (const key of Object.keys(perceptionByModel)) {
    const modelPerception = perceptionByModel[key];
    const scale = Math.max(0.5, Math.min(1.5,
      overallPerception > 0 ? modelPerception / overallPerception : 1,
    ));

    if (exactPerModel[key]) {
      const ex = exactPerModel[key];
      switch (metric) {
        case "perception":
          perModel[key] = Math.round(modelPerception * 100) / 100; break;
        case "referral":
          perModel[key] = Math.round(parseFloat(ex.referral_rate ?? overall * scale) * 100) / 100; break;
        case "bestInCategory":
          perModel[key] = Math.round(parseFloat(ex.best_in_category_pct ?? overall * scale) * 100) / 100; break;
        case "numberOne":
          perModel[key] = Math.round(parseFloat(ex.number_one_recommendation_pct ?? overall * scale) * 100) / 100; break;
      }
      continue;
    }

    const val = metric === "perception" ? modelPerception : Math.min(100, overall * scale);
    perModel[key] = Math.round(val * 100) / 100;
  }

  return { overall, perModel };
}

/**
 * Build a single synthetic data point from antennaData for use in charts
 * when no historical DB snapshots exist yet.
 */
export function buildSyntheticSeries(
  antennaData: any,
  metric: MetricKey,
  selectedModels?: string[],
): SyntheticSeriesPoint[] {
  const extracted = extractMetricFromAntennaData(antennaData, metric);
  if (!extracted) return [];

  const { overall, perModel } = extracted;
  const models = selectedModels?.length ? selectedModels : Object.keys(perModel);

  const date = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const point: SyntheticSeriesPoint = { date, timestamp: 0, overall };
  for (const key of models) {
    // Use the per-model value if available; fall back to overall (never 0)
    point[key] = perModel[key] ?? overall;
  }
  return [point];
}

// ── API calls ─────────────────────────────────────────────────────────────────

// Short-lived request de-duplication. The Performance page renders four metric
// charts that each request the SAME time-series endpoint, and that endpoint does
// DB writes on every call. Without this, one page load fired four (or more, on
// re-render) identical requests in parallel and exhausted the DB connection
// pool. Sharing one in-flight promise per (brand, days) collapses them into a
// single backend call. A short TTL also absorbs rapid re-render bursts. The
// cache is cleared on "analytics:refresh" (Run Now) so fresh data still loads.
// 60s window: every time-series consumer now requests the same (brand, 90) key,
// so within a design/browse session the write-on-read endpoint is hit at most
// once per minute regardless of how many charts render or time-range buttons
// get clicked.
const _TS_TTL_MS = 60_000;
const _tsCache = new Map<string, { at: number; p: Promise<AnalyticsTimeSeriesData> }>();
let _schedCache: { at: number; p: Promise<ScheduleConfig> } | null = null;

if (typeof window !== "undefined") {
  window.addEventListener("analytics:refresh", () => {
    _tsCache.clear();
    _schedCache = null;
  });
}

export async function fetchAnalyticsTimeSeries(
  brand?: string,
  days: number = 90,
): Promise<AnalyticsTimeSeriesData> {
  const brandName = brand || getActiveBrand();
  if (!brandName) throw new Error("NO_BRAND");

  const key = `${brandName}|${days}`;
  const now = Date.now();
  const hit = _tsCache.get(key);
  if (hit && now - hit.at < _TS_TTL_MS) return hit.p;

  const p = apiFetch<AnalyticsTimeSeriesData>(
    `/analytics/time-series?brand=${encodeURIComponent(brandName)}&days=${days}`,
  ).catch((err) => { _tsCache.delete(key); throw err; });

  _tsCache.set(key, { at: now, p });
  return p;
}

export async function fetchScheduleConfig(): Promise<ScheduleConfig> {
  const now = Date.now();
  if (_schedCache && now - _schedCache.at < _TS_TTL_MS) return _schedCache.p;

  const p = apiFetch<ScheduleConfig>("analytics/schedule")
    .catch((err) => { _schedCache = null; throw err; });

  _schedCache = { at: now, p };
  return p;
}

export async function updateScheduleConfig(body: {
  is_enabled: boolean;
  interval_days: number;
  brands: string[];
}): Promise<ScheduleConfig> {
  return apiFetch<ScheduleConfig>("/analytics/schedule", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/**
 * Trigger Run Now.
 * Backend awaits all LLM calls before responding, so data is in DB when resolved.
 * Calls triggerAIBehaviorRefresh() so AI Behavior hooks start polling immediately.
 */
export async function triggerRunNow(brands?: string[]): Promise<any> {
  const result = await apiFetch("/analytics/schedule/run-now", {
    method: "POST",
    body: JSON.stringify({ brands: brands ?? [getActiveBrand()] }),
  });
  triggerAIBehaviorRefresh();
  return result;
}

export async function startScheduler(): Promise<any> {
  return apiFetch("/analytics/schedule/start", { method: "POST" });
}

export async function stopScheduler(): Promise<any> {
  return apiFetch("/analytics/schedule/stop", { method: "POST" });
}

export async function seedBrandSnapshots(brandName: string): Promise<any> {
  return apiFetch("/analytics/seed", {
    method: "POST",
    body: JSON.stringify({ brand_name: brandName }),
  });
}

export async function seedFromPayload(
  brandName: string,
  aggregatedData: Record<string, any>,
): Promise<any> {
  return apiFetch("/analytics/seed-from-payload", {
    method: "POST",
    body: JSON.stringify({ brand_name: brandName, aggregated_data: aggregatedData }),
  });
}

// ── Silent seed waterfall ─────────────────────────────────────────────────────

async function ensureSnapshots(
  brandName: string,
  antennaData?: any,
): Promise<AnalyticsTimeSeriesData> {
  let ts = await fetchAnalyticsTimeSeries(brandName, 90);
  if (ts.snapshot_count > 0) return ts;

  try {
    const r = await seedBrandSnapshots(brandName);
    if ((r.seeded ?? 0) > 0) {
      ts = await fetchAnalyticsTimeSeries(brandName, 90);
      if (ts.snapshot_count > 0) return ts;
    }
  } catch { /* continue */ }

  if (antennaData && typeof antennaData === "object") {
    try {
      await seedFromPayload(brandName, antennaData);
      ts = await fetchAnalyticsTimeSeries(brandName, 90);
      if (ts.snapshot_count > 0) return ts;
    } catch { /* continue */ }
  }

  // Last-resort seed from the most recent brand run cached in localStorage.
  // GUARD: only use it when that cached run actually belongs to THIS brand.
  // Without this check, a brand with no snapshots gets seeded from whatever
  // brand was last run (e.g. Iterate's payload written under Sunbit), which is
  // how cross-brand data contaminated analytics_snapshots. If the cached run's
  // brand can't be matched to the target brand, we skip rather than seed blind.
  try {
    const raw = localStorage.getItem("bm_last_result");
    if (raw) {
      const stored  = JSON.parse(raw);
      const payload = stored?.data ?? stored;
      const payloadBrand = String(
        stored?.brandName ?? stored?.brand_name ?? stored?.brand ??
        payload?.brandName ?? payload?.brand_name ?? payload?.brand ?? "",
      ).trim().toLowerCase();
      const target = brandName.trim().toLowerCase();
      const sameBrand =
        payloadBrand.length > 0 &&
        (payloadBrand === target || payloadBrand.includes(target) || target.includes(payloadBrand));
      if (sameBrand && payload && typeof payload === "object") {
        await seedFromPayload(brandName, payload);
        ts = await fetchAnalyticsTimeSeries(brandName, 90);
      }
    }
  } catch { /* swallow */ }

  return ts;
}

// ── Main overview loader ──────────────────────────────────────────────────────

export async function fetchAnalyticsOverviewData(antennaData?: any): Promise<{
  promptTracking: PromptTrackingData;
  aiInsights: Omit<AIInsight, "icon">[];
}> {
  const brandName = getActiveBrand();
  if (!brandName) throw new Error("NO_BRAND");

  const ts     = await ensureSnapshots(brandName, antennaData);
  const s      = ts.summary;
  const series = ts.perception_series;
  const latest = series[series.length - 1];
  const prev   = series[Math.max(0, series.length - 5)];

  const trendVal =
    latest && prev && prev.overall > 0
      ? (((latest.overall - prev.overall) / prev.overall) * 100).toFixed(1)
      : "0";
  const trendDir: "up" | "down" | "stable" =
    parseFloat(trendVal) > 0 ? "up" : parseFloat(trendVal) < 0 ? "down" : "stable";

  // Anchor the Upmode AI Index KPI to the same composite Brand Monitoring shows
  // (monitoring overallScore) so the two views agree. Fall back to the snapshot
  // perception_score only when the monitoring payload is unavailable.
  const canonicalPerception = extractMetricFromAntennaData(antennaData, "perception");
  const indexScore =
    canonicalPerception && canonicalPerception.overall > 0
      ? Math.round(canonicalPerception.overall)
      : Math.round(s.perception_score);

  // Funnel KPIs (Mention / Top-5 / Primary Recommendation) come from Deep Signal
  // when the brand has scored runs; "Your AI Index" stays anchored to monitoring
  // perception (Deep Signal has no 0–100 perception equivalent). Falls back to
  // the monitoring snapshot numbers when no Deep Signal runs exist.
  const ds = await fetchDeepSignalSeries(brandName).catch(() => null);
  const hasDS = !!ds && ds.runCount > 0 && !!ds.latest;

  // Point-delta vs the previous run, formatted like the existing trend strings.
  // Null when there is no previous run to compare against.
  const dsTrend = (series: DSSeriesPoint[]): string | null => {
    if (series.length < 2) return null;
    // No delta across a series rebaseline: the two reads were measured on
    // different batteries (the tiles label the latest a new baseline).
    if (series[series.length - 1][SEGMENT_KEY] !== series[series.length - 2][SEGMENT_KEY]) return null;
    const d = series[series.length - 1].overall - series[series.length - 2].overall;
    return `${d >= 0 ? "+" : ""}${d.toFixed(1)}%`;
  };

  // A single snapshot has no prior measurement: no trend to report.
  const hasSnapshotTrend = series.length >= 2;
  const snapshotTrend = hasSnapshotTrend
    ? `${parseFloat(trendVal) >= 0 ? "+" : ""}${trendVal}%`
    : null;

  // Canonical Mention Rate: Deep Signal wins when runs exist, otherwise the
  // monitoring snapshot. Every surface on the page reads this ONE number.
  const mentionRateExact = hasDS ? ds!.latest!.mention : s.referral_rate;
  const mentionRate    = Math.round(mentionRateExact);
  const top5Rate       = hasDS ? Math.round(ds!.latest!.top5)       : Math.round(s.best_in_category_pct);
  const primaryRecRate = hasDS ? Math.round(ds!.latest!.primaryRec) : Math.round(s.number_one_recommendation_pct);

  const mentionTrend    = hasDS ? dsTrend(ds!.mention)    : snapshotTrend;
  const top5Trend       = hasDS ? dsTrend(ds!.top5)       : snapshotTrend;
  const primaryRecTrend = hasDS ? dsTrend(ds!.primaryRec) : snapshotTrend;

  const promptTracking: PromptTrackingData = {
    totalPrompts:          ts.snapshot_count,
    promptLimit:           100,
    avgRecommendationRate: primaryRecRate,
    trendDirection:        trendDir,
    trendValue:            snapshotTrend,
    shareOfVoice:          mentionRate,
    shareOfVoiceTrend:     mentionTrend,
    shareOfVoiceRank:      1,
    totalCompetitors:      5,
    sentimentScore:        indexScore,
    sentimentTrend:        snapshotTrend,
    top5Rate,
    top5Trend,
    primaryRecTrend,
    topPerformers:         [],
    needsOptimization:     [],
    latestCompleteness:    hasDS ? ds!.latestCompleteness : null,
    latestIsNewBaseline:   hasDS ? Boolean(ds!.latestSeries?.new_baseline) : false,
    latestRebaseline:      hasDS ? ds!.latestRebaseline : null,
  };

  const aiInsights: Omit<AIInsight, "icon">[] = [
    {
      id: "insight-perception", type: "success",
      iconBg: "var(--success-bg)", iconColor: "var(--success-text)",
      metric: `${s.perception_score.toFixed(1)}`,
      title: "Overall AI perception score",
      subtitle: `From ${ts.snapshot_count} data point${ts.snapshot_count !== 1 ? "s" : ""} across ${ts.selected_models.length} model${ts.selected_models.length !== 1 ? "s" : ""}`,
      details: `Brand perception score is ${s.perception_score.toFixed(1)}/100 averaged across all monitored AI models.`,
      confidence: 90, source: "Analytics • Perception Tracking",
      timestamp: "Latest snapshot",
      aiReasoning: "Score derived from per-model LLM analytics runs.",
    },
    {
      id: "insight-referral",
      type: mentionRateExact >= 60 ? "achievement" : "opportunity",
      iconBg: mentionRateExact >= 60 ? "var(--chart-3-10)" : "var(--signal-blue-10)",
      iconColor: mentionRateExact >= 60 ? "var(--chart-3)" : "var(--signal-blue)",
      metric: `${mentionRateExact.toFixed(1)}%`,
      title: "Mention Rate",
      subtitle: "How often AI models mention or refer to your brand",
      details: `Your brand appears in ${mentionRateExact.toFixed(1)}% of relevant AI conversations.`,
      confidence: 87, source: "Analytics • Mention Tracking",
      timestamp: hasDS ? "Latest Deep Signal run" : "Latest snapshot",
      aiReasoning: "Averaged from per-model mention rates.",
    },
    {
      id: "insight-category",
      type: s.best_in_category_pct >= 40 ? "success" : "warning",
      iconBg: s.best_in_category_pct >= 40 ? "var(--success-bg)" : "var(--warning-bg)",
      iconColor: s.best_in_category_pct >= 40 ? "var(--success-text)" : "var(--warning-text)",
      metric: `${s.best_in_category_pct.toFixed(1)}%`,
      title: "Best in category (top-5) rate",
      subtitle: "How often your brand appears in AI top-5 recommendations",
      details: `Your brand appears in ${s.best_in_category_pct.toFixed(1)}% of AI-generated top-5 recommendation lists.`,
      confidence: 89, source: "Analytics • Competitive Positioning",
      timestamp: "Latest snapshot",
      aiReasoning: "Averaged from per-model best-in-category rates.",
    },
    {
      id: "insight-number-one",
      type: s.number_one_recommendation_pct >= 25 ? "achievement" : "opportunity",
      iconBg: "var(--signal-blue-10)", iconColor: "var(--signal-blue)",
      metric: `${s.number_one_recommendation_pct.toFixed(1)}%`,
      title: "#1 recommendation rate",
      subtitle: "How often your brand is the top pick in AI responses",
      details: `Your brand is the #1 AI recommendation in ${s.number_one_recommendation_pct.toFixed(1)}% of relevant queries.`,
      confidence: 91, source: "Analytics • Recommendation Tracking",
      timestamp: "Latest snapshot",
      aiReasoning: "Averaged from per-model #1 recommendation rates.",
    },
  ];

  return { promptTracking, aiInsights };
}